import React, { useEffect, useState } from "react";
import { X, Mail } from "lucide-react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  updateAdminUser,
  deleteAdminUser,
} from "../../actions/ManageUserAction";
import { formatDate } from "../../utils/DateFormat";
import PortalDropdownBox from "./PortalDropDownBox";
import AccessLevelTable from "./AccessLevelTable";
import ConfirmDialogBoxWithUser from "../layout/ConfirmDialogBoxWithUser";

// List of modules
const MODULES = [
  { key: "dashboard", label: "Dashboard" },
  { key: "order", label: "Orders" },
  {key: "product", label: "Products" },
  { key: "simSwap", label: "Sim swap" },
  { key: "customer", label: "Customers" },
  { key: "referralCode", label: "Referral code" },
  { key: "manageUser", label: "Manage users" },
];

const PERMISSIONS = ["view", "edit", "export"];

const EditUserDetails = ({ user, closeModal, refreshData }) => {
  const [customerName, setCustomerName] = useState(user.name || "N/A");
  const [userStatus, setUserStatus] = useState(
    user.status ? user.status.toUpperCase() : "ACTIVE"
  );
  const [accessControl, setAccessControl] = useState({});
  const [userEmail, setUserEmail] = useState(user.username || "N/A");
  const [emailError, setEmailError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [confirmDeleteDialogBox, setConfirmDeleteDialogBox] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Prepare table format: {view: true, edit: false, ...}
  const normalizeAccessControl = (apiAccess) => {
    const normalized = {};
    MODULES.forEach((m) => {
      normalized[m.key] = {};
      PERMISSIONS.forEach((perm) => {
        normalized[m.key][perm] = apiAccess?.[m.key]?.includes(perm) || false;
      });
    });
    return normalized;
  };

  // Convert table format back to API format: {dashboard: ["view"], ...}
  const prepareAccessLevels = (permissions, originalAccess) => {
    const accessLevels = {};

    Object.keys(permissions).forEach((moduleKey) => {
      const selected = Object.entries(permissions[moduleKey])
        .filter(([perm, value]) => value)
        .map(([perm]) => perm);

      // If user selected some permissions 
      if (selected.length > 0) {
        accessLevels[moduleKey] = selected;
      }
      // If previously had permissions but removed all
      else if (originalAccess?.[moduleKey]) {
        accessLevels[moduleKey] = [];
      }
    });

    return accessLevels;
  };

  useEffect(() => {
    if (user.accessControl) {
      const normalized = normalizeAccessControl(user.accessControl);
      setAccessControl(normalized);
    }
  }, [user]);

  const latestUpdatedAt = user.accessControl?.updatedAt
    ? new Date(user.updatedAt) > new Date(user.accessControl.updatedAt)
      ? user.updatedAt
      : user.accessControl.updatedAt
    : user.updatedAt;

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setUserEmail(value);

    if (value === "") {
      setEmailError("");
    } else if (!isValidEmail(value)) {
      setEmailError("Please enter a valid email address");
    } else {
      setEmailError("");
    }
  };

  const handleEditUser = async () => {
    setEmailError("");

    // Validate email
    if (userEmail !== "" && !isValidEmail(userEmail)) {
      setEmailError("Please enter a valid email address");
      toast.error("Please enter a valid email address.");
      return;
    }

    const payload = {};
    let hasChanges = false;

    if (customerName.trim() !== (user.name || "").trim()) {
      payload.name = customerName.trim();
      hasChanges = true;
    }

    if (userEmail.trim() !== (user.username || "").trim()) {
      payload.username = userEmail.trim();
      hasChanges = true;
    }

    if (userStatus.toLowerCase() !== user.status.toLowerCase()) {
      payload.status = userStatus.toLowerCase();
      hasChanges = true;
    }

    // Compare current form state directly against original API data
    const originalNormalized = normalizeAccessControl(user.accessControl);
    const accessChanged = MODULES.some((m) =>
      PERMISSIONS.some(
        (p) => !!accessControl[m.key]?.[p] !== !!originalNormalized[m.key]?.[p]
      )
    );

    if (accessChanged) {
      const updatedAccess = prepareAccessLevels(
        accessControl,
        user.accessControl
      );
      payload.accessControl = updatedAccess;
      hasChanges = true;
    }

    if (!hasChanges) {
      toast.info("No changes detected.");
      return;
    }

    payload.adminId = user._id;

    try {
      setIsLoading(true);
      const userId = localStorage.getItem("adminId");

      if (!userId) {
        toast.error("User ID not found. Please log in again.");
        return;
      }
      // console.log(formData.keys);
      const response = await updateAdminUser(payload, userId);
      if (response.success) {
        toast.success(
          response?.message || "Admin details updated successfully!"
        );
        setTimeout(() => {
          closeModal();
          refreshData();
        }, 2000);
      } else {
        toast.error(response?.message || "Failed to update admin details.");
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmDeleteDialogBox = (userId) => {
    setSelectedUser(user);
  };

  const handleDeleteUser = async (userId) => {
    try {
      const adminId = localStorage.getItem("adminId"); // Get adminId from localStorage

      if (!adminId) {
        toast.error("Admin ID not found. Please log in again.");
        return;
      }

      const response = await deleteAdminUser(userId, adminId);

      if (response?.success) {
        toast.success(response.message || "User deleted successfully.");
        // setUserName("");
        setConfirmDeleteDialogBox(false);
        refreshData();
      } else {
        toast.error(response?.message || "Failed to delete user.");
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      {/* Mobile close button above modal, top right */}
      <button
        onClick={closeModal}
        className="block md:hidden fixed z-50 p-2 bg-white rounded-full shadow border border-gray-200"
        style={{
          top: '16px',
          right: '16px',
        }}
      >
        <X size={28} />
      </button>
      <div className="relative bg-white p-6 rounded-[20px] w-full max-w-[800px] sm:w-[800px] max-h-[650px] shadow-lg flex flex-col mx-4 sm:mx-0" style={{maxWidth: '95vw'}}>
        {/* Desktop close button in previous position */}
        <button
          onClick={closeModal}
          className="hidden md:block absolute -top-5 -right-10 p-2 bg-white rounded-full"
        >
          <X size={20} />
        </button>

        <div className="flex justify-between items-center pb-4 mb-6 border-b sticky top-0 bg-white z-10 rounded-t-[20px]">
          <div className="justify-start">
            <h2 className="text-3xl font-bold ">Edit User</h2>
            <p className="text-text2 font-normal text-sm pt-1">
              Last updated: {formatDate(latestUpdatedAt)}
            </p>
          </div>

          <div className="gap-3 flex my-1">
            <button
              onClick={() => {
                handleConfirmDeleteDialogBox();
                setConfirmDeleteDialogBox(true);
              }}
              className="btn-delete "
              disabled={isLoading}
            >
              Delete
            </button>
            <button
              onClick={handleEditUser}
              className={`secondary_button ${
                isLoading ? "opacity-50 cursor-not-allowed" : "active"
              }`}
              disabled={isLoading}
            >
              {isLoading ? "Updating..." : "Update"}
            </button>
          </div>
        </div>
        <div
          className="overflow-y-auto flex-1"
          style={{
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
        >
          <style>
            {`
              div::-webkit-scrollbar {
                display: none;
              }
              @media (max-width: 768px) {
                .grid-cols-2 {
                  grid-template-columns: 1fr;
                }
                .w-[800px] {
                  width: 90%;
                  max-width: 400px;
                }
                .flex-between {
                  flex-direction: column;
                  gap: 1rem;
                }
                .p-6 {
                  padding: 1.5rem 1.25rem;
                }
                .gap-3 {
                  gap: 0.5rem;
                }
                .text-3xl {
                  font-size: 1.5rem;
                }
                .w-40 {
                  width: 100%;
                }
              }
            `}
          </style>
          <div className="grid grid-cols-2 gap-3">
            {/* Admin Name Input */}
            <div className="mb-4 space-y-1">
              <label className="font-medium text-sm leading-5 text-text3">
                User name
              </label>
              <input
                type="text"
                placeholder="Enter name"
                className="primary_input text-text1 w-full p-2 border rounded"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                disabled={isLoading}
              />
            </div>

            {/* User Email Address Input */}
            <div className="mb-4 space-y-1">
              <label className="font-medium text-sm leading-5 text-text3">
                Email address
              </label>
              <div className="flex items-center border border-gray-300 pl-3 rounded bg-white">
                <Mail className="text-gray-500 mr-3" />
                <input
                  type="email"
                  placeholder="Enter email address"
                  className="secondary_input text-text1 w-full border-l rounded-md rounded-l-none border-gray-300 bg-white focus:outline-none focus:ring-0"
                  value={userEmail}
                  onChange={handleEmailChange}
                  disabled={isLoading}
                />
              </div>
              {emailError && (
                <p className="text-sm text-required mt-1">{emailError}</p>
              )}
            </div>
          </div>

          {/* Access Level Table */}
          <AccessLevelTable
            modules={MODULES.map((m) => m)}
            value={accessControl}
            onChange={setAccessControl}
          />

          {/* Status Dropdown */}
          <div className="flex justify-between items-center bg-gray-50 p-2 rounded-md mt-[20px] mb-[20px]">
            <div>
              <p className="font-medium">Update status</p>
              <p className="text-sm text-gray-500">
                This will affect the status of the particular user.
              </p>
            </div>
            <div className="w-40">
               <PortalDropdownBox
                optionList={["ACTIVE", "INACTIVE"]}
                styleName="rounded-md text-sm font-normal text-text1 uppercase focus:outline-none focus:ring-0 w-full mt-1"
                value={userStatus.toUpperCase()} 
                handleChange={(selectedValue) =>
                  setUserStatus(selectedValue.toLowerCase())
                }
              />
            </div>
          </div>
        </div>
      </div>
      {confirmDeleteDialogBox && (
        <ConfirmDialogBoxWithUser
          handleCancel={() => setConfirmDeleteDialogBox(false)}
          message="Are you sure you want to <strong> delete this user?</strong> This action is permanent and cannot be undone. Once deleted, this user will loose the access to the portal."
          type="inactive"
          handleConfirm={() => handleDeleteUser(selectedUser._id)}
          confirmMessage="Confirm & Delete"
          hideUsernameInput={true}
        />
      )}
    </div>
  );
};

export default EditUserDetails;